<ng-container *vsLet="permissionChecks$ | async as permissionChecks">
  <vs-dialog
    #dialog
    *ngIf="claim$ | async as claim"
    (closed)="navigateBack(claim.id)"
    [config]="{ dialogContainerClass: 'w-[500px] max-w-[800px]', startOpen: true }"
  >
    <vs-dialog-header
      [config]="{
          mode: 'simple',
          showDivider: true,
          
          icon: 'bank-note-01',
          iconTheme: 'primary',
          
          title: 'Manage payment',
          subtitle: 'Review the details below to issue this payment.'
        }"
    ></vs-dialog-header>

    <ng-container *ngIf="payment$ | async as payment; else loading">
      <div
        *ngIf="payment.status === 'FAILED'"
        class="mb-4 mt-8 mx-2 rounded-xl border border-danger-300 relative overflow-hidden px-3 py-2"
      >
        <div class="bg-danger-50 opacity-50 absolute w-full h-full top-0 left-0 -z-10"></div>
        <div class="grid grid-cols-[auto_1fr] gap-3">
          <div
            class="w-10 min-w-[40px] h-10 flex items-center justify-center bg-base rounded-lg border border-danger-200"
          >
            <vs-icon name="alert-circle" size="20" class="text-danger-700"></vs-icon>
          </div>
          <div>
            <p class="font-semibold text-danger-700">Payment failed</p>
            <p class="text-sm font-normal text-danger-700">
              This payment can't be changed or sent again. To try again, create a new payment — it stays linked to the
              same authorization.
            </p>
          </div>
        </div>
      </div>

      <div
        *ngIf="(!payment?.destination && !isShopPayee(payment)) || payment.amount <= 0"
        class="mb-4 mt-8 text-danger-600 mx-2 text-sm"
      >
        <div class="flex flex-col gap-1">
          <span>to be able to issue this payment, please complete all the details of the payment.</span>
          <ul class="list-disc ml-8">
            <li *ngIf="!payment?.destination && !isShopPayee(payment)">Destination is Missing</li>
            <li *ngIf="payment.amount <= 0">Amount should be more than 0</li>
          </ul>
        </div>
      </div>

      <div class="border rounded-xl mb-4 mx-2 w-fit" [class.mt-8]="payment.status !== 'FAILED'">
        <div class="flex flex-col gap-4 p-6">
          <div class="flex flex-col gap-2.5">
            <div class="flex text-lg justify-between text-gray-700 items-center">
              <div class="flex items-center gap-2">
                {{ payment.title }}

                <vs-badge [theme]="payment.destination === pending ? 'warn' : status[payment.status]">
                  {{ payment.destination === pending ? 'Pending customer' : payment.status | titlecase }}
                </vs-badge>
              </div>

              <span>{{ (payment.amount / 100) | currency }}</span>
            </div>

            <div class="text-gray-500 text-sm" *ngIf="payment.description">{{ payment.description }}</div>

            <div class="text-sm" *ngIf="payeeLabel(payment) as payee">Payee: {{ payee }}</div>
            <div class="text-sm text-gray-500" *ngIf="isShopPayee(payment)">
              Shop payments can't be sent through Dwolla yet. Mark the payment as paid once the shop is paid.
            </div>

            <div class="text-sm">{{ payment.destinationName }}</div>
            <div class="text-sm">{{ payment.referenceId ?? '' }}</div>

            <ng-container *ngIf="payment.links.length">
              <ng-container *ngFor="let link of payment.links">
                <vs-info-card
                  *ngIf="link.link.claimId_1 !== payment.claimId && link.link.claimId_2 !== payment.claimId && !link.link.activityId_1"
                  class="flex-grow max-w-[50%]"
                  [config]="link.card"
                >
                  <vs-badge
                    *ngIf="link.link.claimItem_1 || link.link.claimItem_2"
                    slot="header-end"
                    [theme]="getItemTheme(link.link.claimItem_1 || link.link.claimItem_2 || undefined)"
                  >
                    {{ link.link.claimItem_1?.status || link.link.claimItem_2?.status | titlecase }}
                  </vs-badge>
                </vs-info-card>
              </ng-container>
            </ng-container>
          </div>
        </div>
      </div>

      <div class="px-2 pb-2 flex justify-between">
        <vs-input-group>
          <button
            *ngIf="permissionChecks?.canVoid && payment.status === paymentStatus.DRAFT"
            vs-button
            appearance="outline"
            (click)="confirmVoid.open()"
          >
            Void
          </button>

          <ng-container *ngIf="claim$ | async as claim">
            <button
              *ngIf="permissionChecks?.canEdit && payment.status === paymentStatus.DRAFT"
              vs-button
              appearance="outline"
              (click)="update(payment, claim)"
            >
              Edit
            </button>
          </ng-container>
        </vs-input-group>

        <div class="flex gap-3 items-center">
          <button vs-button appearance="outline" (click)="dialog.close()">Close</button>
          <ng-container *ngIf="claim$ | async as claim">
            <button
              *ngIf="permissionChecks?.canEdit && payment.status === paymentStatus.FAILED"
              vs-button
              theme="primary"
              [disabled]="!canRetry(payment, claim)"
              (click)="retry(payment, claim)"
            >
              Create new payment
            </button>
          </ng-container>
          <ng-container *ngIf="claim$ | async as claim">
            <button
              *ngIf="permissionChecks?.canEdit && payment.status === paymentStatus.DRAFT && isShopPayee(payment)"
              vs-button
              theme="primary"
              [disabled]="payment.amount <= 0"
              (click)="preparePay('markPaid', claim); confirmPay.open()"
            >
              Mark as paid
            </button>
          </ng-container>
          <button
            *ngIf="permissionChecks?.canIssue && payment.status === paymentStatus.DRAFT && !isShopPayee(payment)"
            vs-button
            theme="primary"
            appearance="default"
            [disabled]="payment.amount === 0 || !payment.destination || payment.destination === pending"
            (click)="preparePay('issue', claim); confirmPay.open()"
          >
            Issue Payment
          </button>
        </div>
      </div>

      <vs-dialog #confirmVoid [config]="{ dialogContainerClass: 'w-[500px] max-w-[800px]' }">
        <vs-dialog-header
          [config]="{
                  mode: 'simple',
                  showDivider: true,
                  
                  icon: 'bank-note-01',
                  iconTheme: 'warn',
                  
                  title: 'Void payment',
                  subtitle: 'Are you sure you want to void this payment? This action cannot be undone.'
                }"
        ></vs-dialog-header>

        <div class="flex gap-3 pt-3 px-2 pb-2">
          <button class="basis-1/2" vs-button appearance="outline" (click)="confirmVoid.close()">Cancel</button>
          <button
            class="basis-1/2"
            vs-button
            appearance="default"
            theme="danger"
            (click)="voidPayment(payment.id, payment.claimId, payment.title);confirmVoid.close()"
          >
            Void Payment
          </button>
        </div>
      </vs-dialog>

      <vs-dialog #confirmPay [config]="{ dialogContainerClass: 'w-[350px] sm:w-[520px] max-w-[90%] max-h-[90%]' }">
        <vs-dialog-header
          [config]="{
            mode: 'simple',
            showDivider: true,
            icon: 'lock-01',
            iconTheme: 'warn',
            title: lockConfig[payAction].header.title,
            subtitle: lockConfig[payAction].header.subtitle(payment.title)
          }"
        ></vs-dialog-header>

        <ws-claim-lock-notice *ngIf="payNotice" class="block mt-4" [notice]="payNotice"></ws-claim-lock-notice>

        <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
          <button vs-button appearance="outline" class="w-full sm:w-auto" (click)="confirmPay.close()">
            {{ cancel }}
          </button>
          <button
            vs-button
            theme="warn"
            class="w-full sm:w-auto"
            data-testid="confirm-payment"
            (click)="confirmPay.close(); payAction === 'issue' ? issue(payment.id, payment.claimId, payment.title) : markAsPaid(payment, claim)"
          >
            {{ lockConfig[payAction].confirm }}
          </button>
        </div>
      </vs-dialog>
    </ng-container>

    <ng-container *ngIf="permissionChecks?.canEdit">
      <ng-container *vsLet="destinations$ | async as destinations">
        <ng-container *vsLet="customer$ | async as customer">
          <ng-container *vsLet="customerId$ | async as customerId">
            <ws-claim-payment-dialog
              [destinations]="destinations"
              [customer]="customer"
              [customerId]="customerId?.id ?? null"
              [canManagePaymentMethods]="permissionChecks?.canManagePaymentMethods ?? false"
              (refresh)="refreshAfterClose = true; dialog.close()"
            ></ws-claim-payment-dialog>
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>

    <ng-template #loading>
      <div class="flex justify-center items-center h-60">
        <vs-icon name="loading-01" size="25" class="animate-spin text-primary"></vs-icon>
      </div>
    </ng-template>
  </vs-dialog>
</ng-container>
